import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlCreationOptions } from '@angular/router';
import { AppUpdateInfo } from '@pxl/models/common';
import { CommonService, PlatformService } from '@pxl/models/contracts';
import { AppRoutingRouteNames } from '@pxl/route-names';
import { clearOutdatedAppReloadMarker, getSkipAppNeedsUpdateStorage, reloadOutdatedBrowserApp } from '@pxl/shared/util';

export const appNeedsUpdateGuard = (onlyMust = false): CanActivateFn => {
    return async (activatedRoute, routerState) => {
        const commonService = inject(CommonService);
        const router = inject(Router);
        const platformService = inject(PlatformService);
        const appUpdateInfo: AppUpdateInfo = await commonService.appUpdateInfo();
        if (!platformService.isNativePlatform()) {
            // There is no app store to send a browser user to. An unsupported version means the
            // browser runs a stale build, so re-fetch index.html instead of showing the update page.
            // Blocking the route is only correct while the reload is actually on its way. If it was
            // already attempted, the build is stale for good and the user still needs a usable app.
            if (appUpdateInfo.MustUpdate) {
                return !reloadOutdatedBrowserApp();
            }

            clearOutdatedAppReloadMarker();
            return true;
        }

        const skipAppNeedsUpdateUrl = activatedRoute.queryParams['skipAppNeedsUpdate'] === 'true';
        const skipAppNeedsUpdateStorage = getSkipAppNeedsUpdateStorage();

        if (
            !appUpdateInfo.anyUpdateAvailable() ||
            skipAppNeedsUpdateUrl ||
            skipAppNeedsUpdateStorage ||
            (onlyMust && !appUpdateInfo.MustUpdate)
        ) {
            return true;
        }

        const navigationExtras: UrlCreationOptions = {
            queryParams: {
                destinationUrl: routerState.url,
                canUpdate: appUpdateInfo.CanUpdate,
                mustUpdate: appUpdateInfo.MustUpdate,
                shouldUpdate: appUpdateInfo.ShouldUpdate,
            },
        };

        return router.createUrlTree([AppRoutingRouteNames.AppUpdateInfo], navigationExtras);
    };
};
